// src/components/Notifications.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Switch,
  Collapse,
  IconButton,
  Alert,
  Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import TopBar from './TopBar';
import { RoundedCard } from './shared/StyledComponents';
import CakeIcon from '@mui/icons-material/Cake';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ReplayIcon from '@mui/icons-material/Replay';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import SendIcon from '@mui/icons-material/Send';
import CheckIcon from '@mui/icons-material/Check';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { useParams } from 'react-router-dom';

const PageContainer = styled(Box)(() => ({
  minHeight: '100vh',
  backgroundColor: '#f7f7f8',
  display: 'flex',
  flexDirection: 'column',
}));

const ContentContainer = styled(Container)(({ theme }) => ({
  flex: 1,
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(6),
  [theme.breakpoints.down('sm')]: {
    paddingTop: theme.spacing(2.5),
    paddingBottom: theme.spacing(4),
  },
}));

// Clean, flat card — no gradient bars, no hover lift.
const NotificationCard = styled(RoundedCard)(() => ({
  background: '#ffffff',
  border: '1px solid #ececee',
  borderRadius: 16,
  boxShadow: 'none',
  overflow: 'hidden',
  transition: 'border-color 0.15s ease',
  '&:hover': { borderColor: '#dcdce0' },
}));

const HeaderRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  padding: theme.spacing(2.5),
  gap: theme.spacing(2),
}));

const IconBadge = styled(Box)(({ $bgColor = '#f4f4f5' }) => ({
  width: 44,
  height: 44,
  borderRadius: 10,
  backgroundColor: $bgColor,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledTextField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': { borderRadius: 10 },
}));

// A placeholder inserter — one consistent control for every dynamic value.
const TokenButton = styled(Button)(() => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.8rem',
  borderRadius: 8,
  padding: '5px 12px',
  color: '#3f3f46',
  borderColor: '#e4e4e7',
  backgroundColor: '#fff',
  '&:hover': { backgroundColor: '#f4f4f5', borderColor: '#d4d4d8' },
}));

// Highlighted span used in the live preview to show what gets personalised.
const Token = styled('span')(() => ({
  backgroundColor: 'rgba(255, 183, 27, 0.18)',
  color: '#8a6300',
  borderRadius: 5,
  padding: '1px 5px',
  fontWeight: 600,
  whiteSpace: 'nowrap',
}));

// The three dynamic values a message can contain. `token` is the exact string
// stored in the message and substituted by the backend — do not rename.
const PLACEHOLDERS = [
  { token: '[Name]', label: 'Customer name', icon: PersonOutlineIcon },
  { token: '[Business Name]', label: 'Business name', icon: StorefrontOutlinedIcon },
  { token: '[Promotion]', label: 'Special offer', icon: LocalOfferIcon },
];

const PLACEHOLDER_RE = /(\[Name\]|\[Business Name\]|\[Promotion\])/g;

const DEFAULT_NOTIFICATIONS = {
  reminder1: {
    id: 'reminder1',
    name: 'First Visit Reminder',
    message: "Hey [Name], we haven't seen you in a while and we miss you at [Business Name]! Drop by soon - it'll be great to catch up",
    triggerDays: 14,
    enabled: true,
    icon: AccessTimeIcon,
    iconBg: '#eaf2fe',
    iconColor: '#1976d2',
    accentColor: '#1976d2',
    promotion: '',
  },
  reminder2: {
    id: 'reminder2',
    name: 'Second Visit Reminder',
    message: "Hey [Name], we've really missed seeing you at [Business Name]. As a thank-you, here's [Promotion] for your next visit! We'd love to welcome you back.",
    triggerDays: 21,
    enabled: false,
    icon: FavoriteBorderIcon,
    iconBg: '#fdeaf1',
    iconColor: '#e91e63',
    accentColor: '#e91e63',
    promotion: '',
    isOptional: true,
  },
  birthday: {
    id: 'birthday',
    name: 'Birthday Message',
    message: "Happy Birthday, [Name]! We're celebrating with you! Here's [Promotion] at [Business Name]. Treat yourself! You deserve it.",
    triggerDescription: "Sent on the customer's birthday",
    enabled: true,
    icon: CakeIcon,
    iconBg: '#fff2e0',
    iconColor: '#ff9800',
    accentColor: '#ff9800',
    promotion: '',
  },
  loyaltyThankYou: {
    id: 'loyaltyThankYou',
    name: 'Loyalty Thank You',
    message: "Hey [Name], thank you for being part of the [Business Name] team! We truly appreciate your loyalty. As a token of our gratitude, here's [Promotion] just for you. Keep being awesome!",
    triggerDescription: 'Sent to regular returning customers once a month',
    enabled: true,
    icon: FavoriteBorderIcon,
    iconBg: '#e7f6e9',
    iconColor: '#4caf50',
    accentColor: '#4caf50',
    promotion: '',
  },
};

const NOTIF_KEYS = ['reminder1', 'reminder2', 'birthday', 'loyaltyThankYou'];

const plural = (n) => `${n} day${n === 1 ? '' : 's'}`;

// The reminder schedule is driven by the vendor's business category: the first
// reminder uses the category's configured days, later ones chain off it. Derive
// the caption from the current values rather than hardcoding numbers.
const describeTrigger = (notif, all) => {
  if (notif.id === 'reminder1') {
    return notif.triggerDays
      ? `Sent ${plural(notif.triggerDays)} after last visit`
      : 'Sent after the last visit';
  }
  if (notif.id === 'reminder2') {
    const gap = (notif.triggerDays || 0) - (all.reminder1?.triggerDays || 0);
    return gap > 0
      ? `Sent ${plural(gap)} after the first reminder (optional)`
      : 'Optional follow-up reminder';
  }
  return notif.triggerDescription;
};

// Short label for each reminder type, used by the history rows.
const TYPE_LABELS = {
  reminder1: 'First reminder',
  reminder2: 'Second reminder',
  birthday: 'Birthday',
  loyaltyThankYou: 'Loyalty thank you',
};

const TYPE_COLORS = {
  reminder1: { bg: '#eaf2fe', text: '#1565c0' },
  reminder2: { bg: '#fdeaf1', text: '#c2185b' },
  birthday: { bg: '#fff2e0', text: '#e65100' },
  loyaltyThankYou: { bg: '#e7f6e9', text: '#2e7d32' },
};

const HISTORY_PAGE_SIZE = 25;

// "2 hours ago" / "3 days ago" — falls back to a date once it stops being useful.
const timeAgo = (value) => {
  const then = new Date(value).getTime();
  if (isNaN(then)) return '';
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  return new Date(value).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

export default function Notifications() {
  const { userId } = useParams();
  const [notifications, setNotifications] = useState(DEFAULT_NOTIFICATIONS);
  const [expandedSections, setExpandedSections] = useState({});
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [businessName, setBusinessName] = useState('');
  const [history, setHistory] = useState([]);
  const [historyCounts, setHistoryCounts] = useState(null);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyFilter, setHistoryFilter] = useState('all');
  const [historyCursor, setHistoryCursor] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(true);
  const textFieldRefs = useRef({});

  // Load a page of sent-reminder history. `cursor` continues the current list;
  // omitting it starts a fresh one (used when the type filter changes).
  const loadHistory = useCallback(async (type, cursor) => {
    if (!userId) return;
    setHistoryLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ limit: String(HISTORY_PAGE_SIZE) });
      if (type && type !== 'all') params.set('type', type);
      if (cursor) params.set('before', cursor);

      const res = await fetch(`/api/notifications/${userId}/history?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load history');
      const data = await res.json();

      setHistory(prev => (cursor ? [...prev, ...(data.entries || [])] : (data.entries || [])));
      setHistoryCounts(data.counts || null);
      setHistoryTotal(data.total || 0);
      setHistoryCursor(data.nextCursor || null);
    } catch (err) {
      console.error('Error loading notification history:', err);
      if (!cursor) setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [userId]);

  const mergeSettings = useCallback((settings) => {
    setNotifications(prev => {
      const updated = { ...prev };
      for (const key of NOTIF_KEYS) {
        const saved = settings[key];
        if (saved) {
          updated[key] = {
            ...updated[key],
            enabled: saved.enabled ?? updated[key].enabled,
            message: saved.message || updated[key].message,
            triggerDays: saved.triggerDays ?? updated[key].triggerDays,
            promotion: saved.promotion ?? updated[key].promotion,
          };
        }
      }
      return updated;
    });
  }, []);

  useEffect(() => {
    if (!userId) return;
    const token = localStorage.getItem('token');

    fetch(`/api/vendor/${userId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.vendor?.company) setBusinessName(data.vendor.company); })
      .catch(() => {});

    fetch(`/api/notifications/${userId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.settings) mergeSettings(data.settings); })
      .catch(() => {});
  }, [userId, mergeSettings]);

  // Reload from the top whenever the type filter changes.
  useEffect(() => { loadHistory(historyFilter, null); }, [historyFilter, loadHistory]);

  const handleRegenerate = async () => {
    if (regenerating) return;
    setRegenerating(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/vendor/${userId}/regenerate-notifications`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed');
      const settingsRes = await fetch(`/api/notifications/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await settingsRes.json();
      if (data?.settings) mergeSettings(data.settings);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Regenerate failed:', err);
    } finally {
      setRegenerating(false);
    }
  };

  const toggleSection = (section) => setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));

  const updateNotification = (id, field, value) => {
    setNotifications(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const hasPromotion = (msg) => msg.includes('[Promotion]');

  // Insert a placeholder token at the cursor (or end) of a message.
  const insertToken = useCallback((notifId, token) => {
    const el = textFieldRefs.current[notifId];
    const msg = notifications[notifId].message;

    // If the offer token is already present, don't duplicate it — just focus
    // the offer field so the vendor can fill it in.
    if (token === '[Promotion]' && hasPromotion(msg)) {
      const offerEl = textFieldRefs.current[`${notifId}__offer`];
      if (offerEl) offerEl.focus();
      return;
    }

    if (el) {
      const start = el.selectionStart ?? msg.length;
      const end = el.selectionEnd ?? msg.length;
      const newMsg = msg.slice(0, start) + token + msg.slice(end);
      updateNotification(notifId, 'message', newMsg);
      setTimeout(() => {
        el.focus();
        const pos = start + token.length;
        el.setSelectionRange(pos, pos);
      }, 0);
    } else {
      updateNotification(notifId, 'message', msg + token);
    }
  }, [notifications]);

  // Cleanly strip the offer token (and its lead-in) from a message.
  const removeOffer = (notifId) => {
    const msg = notifications[notifId].message;
    const cleaned = msg
      .replace(/,?\s*here's \[Promotion\]/gi, '')
      .replace(/\[Promotion\]\s*/gi, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
    updateNotification(notifId, 'message', cleaned);
    updateNotification(notifId, 'promotion', '');
  };

  const handleSaveAutomated = async (id) => {
    const notif = notifications[id];
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/notifications/${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          [id]: {
            enabled: notif.enabled,
            message: notif.message,
            triggerDays: notif.triggerDays,
            promotion: notif.promotion,
          },
        }),
      });
      if (res.ok) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch (err) {
      console.error('Error saving notification:', err);
    }
  };

  // Render the message with the dynamic values filled in and highlighted, so
  // it's obvious exactly what each customer will receive.
  const renderPreview = (message, promotion) => {
    const values = {
      '[Name]': 'Sarah',
      '[Business Name]': businessName || 'Your Business',
      '[Promotion]': promotion || 'your offer',
    };
    return message.split(PLACEHOLDER_RE).map((part, i) =>
      values[part] !== undefined ? <Token key={i}>{values[part]}</Token> : <span key={i}>{part}</span>
    );
  };

  const renderNotification = (notif) => {
    const Icon = notif.icon;
    const isExpanded = expandedSections[notif.id];
    const offerOn = hasPromotion(notif.message);

    return (
      <NotificationCard key={notif.id} sx={{ mb: 2, p: 0 }}>
        <HeaderRow onClick={() => toggleSection(notif.id)}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
            <IconBadge $bgColor={notif.iconBg}>
              <Icon sx={{ color: notif.iconColor, fontSize: 22 }} />
            </IconBadge>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontWeight: 700, color: '#18181b', fontSize: '1rem' }}>
                  {notif.name}
                </Typography>
                {notif.isOptional && (
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#a1a1aa', border: '1px solid #e4e4e7', borderRadius: 6, px: 0.75 }}>
                    Optional
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                <ScheduleIcon sx={{ fontSize: 13, color: '#a1a1aa' }} />
                <Typography sx={{ fontSize: '0.8rem', color: '#71717a' }}>{describeTrigger(notif, notifications)}</Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: notif.enabled ? '#16a34a' : '#a1a1aa' }}>
              {notif.enabled ? 'On' : 'Off'}
            </Typography>
            <Switch
              checked={notif.enabled}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => updateNotification(notif.id, 'enabled', e.target.checked)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: notif.accentColor },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: notif.accentColor },
              }}
            />
            <IconButton size="small">{isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
          </Box>
        </HeaderRow>

        <Collapse in={isExpanded}>
          <Box sx={{ px: 2.5, pb: 2.5, pt: 0.5, borderTop: '1px solid #f1f1f3' }}>
            {/* Message editor */}
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#3f3f46', mt: 2.5, mb: 1 }}>
              Message
            </Typography>
            <StyledTextField
              multiline
              rows={4}
              fullWidth
              value={notif.message}
              onChange={(e) => updateNotification(notif.id, 'message', e.target.value)}
              inputRef={(el) => { textFieldRefs.current[notif.id] = el; }}
            />

            {/* Placeholder inserter — one consistent system */}
            <Box sx={{ mt: 1.5 }}>
              <Typography sx={{ fontSize: '0.75rem', color: '#71717a', mb: 0.75 }}>
                Add a personalised value — it's filled in automatically for each customer:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {PLACEHOLDERS.map((p) => {
                  const PIcon = p.icon;
                  const active = p.token === '[Promotion]' && offerOn;
                  return (
                    <TokenButton
                      key={p.token}
                      variant="outlined"
                      startIcon={<PIcon sx={{ fontSize: '16px !important' }} />}
                      onClick={() => insertToken(notif.id, p.token)}
                      sx={active ? { borderColor: '#c7d2fe', backgroundColor: '#f5f7ff', color: '#4338ca' } : {}}
                    >
                      {p.label}{active ? ' ✓' : ''}
                    </TokenButton>
                  );
                })}
              </Box>
            </Box>

            {offerOn && !notif.promotion.trim() && (
              <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
                This message mentions an offer but you haven&rsquo;t said what it is. Until you
                do, that sentence is removed before sending &mdash; the rest of the message still
                goes out. Fill in the offer below to include it.
              </Alert>
            )}

            {/* Offer detail — appears only when the offer token is in the message */}
            <Collapse in={offerOn}>
              <Box sx={{ mt: 2, p: 2, borderRadius: 2.5, border: '1px solid #eef0f2', backgroundColor: '#fafafb' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#3f3f46' }}>
                    What's the offer?
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<CloseIcon sx={{ fontSize: '14px !important' }} />}
                    onClick={() => removeOffer(notif.id)}
                    sx={{ textTransform: 'none', fontSize: '0.72rem', color: '#a1a1aa', minWidth: 0 }}
                  >
                    Remove offer
                  </Button>
                </Box>
                <StyledTextField
                  fullWidth
                  size="small"
                  placeholder="e.g. 15% off your next order, a free pastry"
                  value={notif.promotion}
                  onChange={(e) => updateNotification(notif.id, 'promotion', e.target.value)}
                  inputRef={(el) => { textFieldRefs.current[`${notif.id}__offer`] = el; }}
                  InputProps={{ startAdornment: <LocalOfferIcon sx={{ mr: 1, fontSize: 18, color: '#a1a1aa' }} /> }}
                />
              </Box>
            </Collapse>

            {/* Timing (not applicable to birthday) */}
            {notif.id !== 'birthday' && notif.triggerDays !== undefined && (
              <Box sx={{ mt: 2.5 }}>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#3f3f46', mb: 1 }}>
                  Send this many days after the last visit
                </Typography>
                <StyledTextField
                  type="number"
                  size="small"
                  value={notif.triggerDays || ''}
                  onChange={(e) => updateNotification(notif.id, 'triggerDays', parseInt(e.target.value) || 0)}
                  sx={{ width: 160 }}
                  InputProps={{ inputProps: { min: 1, max: 365 } }}
                />
              </Box>
            )}

            {/* Live preview as a message bubble */}
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#3f3f46', mt: 2.5, mb: 1 }}>
              Preview
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
              <IconBadge $bgColor={notif.iconBg} sx={{ width: 32, height: 32, borderRadius: '50%' }}>
                <Icon sx={{ color: notif.iconColor, fontSize: 16 }} />
              </IconBadge>
              <Box sx={{
                background: '#f4f4f5',
                borderRadius: '4px 16px 16px 16px',
                px: 2,
                py: 1.5,
                maxWidth: 520,
              }}>
                <Typography sx={{ fontSize: '0.9rem', color: '#27272a', lineHeight: 1.55 }}>
                  {renderPreview(notif.message, notif.promotion)}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                startIcon={<CheckIcon />}
                onClick={() => handleSaveAutomated(notif.id)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 8,
                  boxShadow: 'none',
                  px: 3,
                  color: '#fff',
                  backgroundColor: notif.accentColor,
                  '&:hover': { backgroundColor: notif.accentColor, filter: 'brightness(0.94)', boxShadow: 'none' },
                }}
              >
                Save changes
              </Button>
            </Box>
          </Box>
        </Collapse>
      </NotificationCard>
    );
  };

  const renderHistory = () => {
    const filters = [
      { id: 'all', label: 'All' },
      ...NOTIF_KEYS.map(key => ({ id: key, label: TYPE_LABELS[key] })),
    ];

    return (
      <NotificationCard sx={{ mt: 4, p: 0 }}>
        <Box sx={{ p: 2.5, borderBottom: '1px solid #f1f1f3' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconBadge $bgColor="#f4f4f5">
              <HistoryIcon sx={{ color: '#52525b', fontSize: 22 }} />
            </IconBadge>
            <Box>
              <Typography sx={{ fontWeight: 700, color: '#18181b', fontSize: '1rem' }}>
                Sent history
              </Typography>
              <Typography sx={{ fontSize: '0.8rem', color: '#71717a', mt: 0.25 }}>
                {historyTotal === 0
                  ? 'Reminders you have sent will appear here'
                  : `${historyTotal} reminder${historyTotal === 1 ? '' : 's'} sent to your customers`}
              </Typography>
            </Box>
          </Box>

          {/* Filter by reminder type. Counts come from the whole history, not
              just the loaded page, so they don't shift as you scroll. */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
            {filters.map((f) => {
              const active = historyFilter === f.id;
              const count = f.id === 'all' ? historyTotal : historyCounts?.[f.id];
              return (
                <Button
                  key={f.id}
                  size="small"
                  onClick={() => setHistoryFilter(f.id)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.78rem',
                    borderRadius: 8,
                    px: 1.5,
                    minWidth: 0,
                    color: active ? '#18181b' : '#71717a',
                    backgroundColor: active ? '#f4f4f5' : 'transparent',
                    border: `1px solid ${active ? '#d4d4d8' : '#ececee'}`,
                    '&:hover': { backgroundColor: '#f4f4f5' },
                  }}
                >
                  {f.label}
                  {historyCounts && count !== undefined ? ` (${count})` : ''}
                </Button>
              );
            })}
          </Box>
        </Box>

        {history.length === 0 && !historyLoading && (
          <Box sx={{ px: 2.5, py: 5, textAlign: 'center' }}>
            <SendIcon sx={{ fontSize: 28, color: '#d4d4d8' }} />
            <Typography sx={{ color: '#71717a', fontSize: '0.9rem', mt: 1.5 }}>
              {historyFilter === 'all'
                ? 'Nothing sent yet. Reminders go out automatically once a customer becomes due.'
                : 'No reminders of this type have been sent yet.'}
            </Typography>
          </Box>
        )}

        {history.map((entry) => {
          const colors = TYPE_COLORS[entry.type] || { bg: '#f4f4f5', text: '#52525b' };
          return (
            <Box
              key={entry._id}
              sx={{
                px: 2.5,
                py: 2,
                borderBottom: '1px solid #f6f6f7',
                '&:last-of-type': { borderBottom: 'none' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.75 }}>
                <Typography sx={{
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  color: colors.text,
                  backgroundColor: colors.bg,
                  borderRadius: 6,
                  px: 0.9,
                  py: 0.2,
                }}>
                  {TYPE_LABELS[entry.type] || entry.type}
                </Typography>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#3f3f46' }}>
                  {entry.customerName || 'Customer'}
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: '#a1a1aa', ml: 'auto' }}>
                  {timeAgo(entry.sentAt)}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '0.88rem', color: '#52525b', lineHeight: 1.55 }}>
                {entry.message}
              </Typography>
              {entry.devicesReached > 0 && (
                <Typography sx={{ fontSize: '0.72rem', color: '#a1a1aa', mt: 0.5 }}>
                  Pushed to {entry.devicesReached} device{entry.devicesReached === 1 ? '' : 's'}
                </Typography>
              )}
            </Box>
          );
        })}

        {historyCursor && (
          <Box sx={{ p: 2, textAlign: 'center', borderTop: '1px solid #f1f1f3' }}>
            <Button
              size="small"
              disabled={historyLoading}
              onClick={() => loadHistory(historyFilter, historyCursor)}
              sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.82rem', color: '#3f3f46' }}
            >
              {historyLoading ? 'Loading…' : 'Load more'}
            </Button>
          </Box>
        )}
      </NotificationCard>
    );
  };

  return (
    <PageContainer>
      <TopBar />
      <ContentContainer maxWidth="xl">
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, fontWeight: 800, color: '#111', mb: 0.5 }}>
              Notifications
            </Typography>
            <Typography variant="body1" sx={{ color: '#71717a' }}>
              Automated messages sent to your customers based on their activity.
            </Typography>
          </Box>
          <Button
            size="small"
            startIcon={<ReplayIcon sx={{ fontSize: 16 }} />}
            onClick={handleRegenerate}
            disabled={regenerating}
            sx={{
              textTransform: 'none',
              borderRadius: 8,
              fontWeight: 600,
              fontSize: '0.8rem',
              color: '#3f3f46',
              border: '1px solid #e4e4e7',
              px: 2,
              whiteSpace: 'nowrap',
              '&:hover': { bgcolor: '#f4f4f5', borderColor: '#d4d4d8' },
            }}
          >
            {regenerating ? 'Regenerating…' : 'Regenerate from brand story'}
          </Button>
        </Box>

        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSaveSuccess(false)}>
            Saved.
          </Alert>
        )}

        {NOTIF_KEYS.map(key => renderNotification(notifications[key]))}

        {renderHistory()}
      </ContentContainer>
    </PageContainer>
  );
}
